import { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import hgTree from '@/assets/hg_tree.png';

// Public, token-gated "add a card on file" page for approved fitters.
//
// Like FitterSigning, this page renders OUTSIDE the Clerk-gated app, so there's no
// auth interceptor in play — it talks to public endpoints under /api/public.
//
// PCI SAFETY: the raw card number + CVV are entered ONLY inside Stax's secure
// iframes (mounted into #card-number / #card-cvv). The card data goes straight
// from the browser into Stax's vault via Stax.js tokenize(). Our backend NEVER
// sees the card number/CVV — we only ever receive Stax's returned references
// (payment_method_id + customer_id, and the display-safe last-four / brand).
const API = '/api';

// Stax.js — exact values per https://docs.staxpayments.com/docs/tokenizing-a-credit-card
const STAX_JS_SRC = 'https://staxjs.staxpayments.com/staxjs-captcha.js';
// Read through a function, not a module-level constant. webpack still inlines the value
// at build time, so production behaviour is identical — but a module-level const is fixed
// at import, which made the real-card branch impossible to drive in a test (jest has no
// .env, so the imported module permanently believed card setup was unconfigured). The
// branch that had never worked in production was also the branch nothing could exercise.
function staxWebToken() {
  return process.env.REACT_APP_STAX_WEB_TOKEN || '';
}

const MONTHS = ['01', '02', '03', '04', '05', '06', '07', '08', '09', '10', '11', '12'];
// Stax wants a 4-digit year. Twenty years is past any card's expiry.
const YEARS = Array.from({ length: 20 }, (_, i) => String(new Date().getFullYear() + i));

/** One labelled field with its own error line, so a validation message sits beside the
 *  thing it is about rather than in a banner at the bottom. */
function Fld({ label, err, children }) {
  return (
    <div className="f">
      <label>{label}</label>
      {children}
      {err && <div className="fielderr" role="alert">{err}</div>}
    </div>
  );
}

// Field config for Stax.js — the `id` values are the container divs Stax mounts
// its secure iframes into. The card number/CVV live only inside those iframes.
const STAX_FIELDS = {
  number: {
    id: 'card-number',
    placeholder: '0000 0000 0000 0000',
    style: 'height:42px;width:100%;font-size:15px;color:#1A1A1A;font-family:-apple-system,BlinkMacSystemFont,"Segoe UI",Roboto,Helvetica,Arial,sans-serif;',
    type: 'text',
    format: 'prettyFormat',
  },
  cvv: {
    id: 'card-cvv',
    placeholder: 'CVV',
    style: 'height:42px;width:100%;font-size:15px;color:#1A1A1A;font-family:-apple-system,BlinkMacSystemFont,"Segoe UI",Roboto,Helvetica,Arial,sans-serif;',
    type: 'text',
  },
};

// Inject the Stax.js script once. Resolves the global StaxJs constructor, or
// rejects if the script can't be reached.
function loadStaxJs() {
  return new Promise((resolve, reject) => {
    if (typeof window !== 'undefined' && window.StaxJs) {
      resolve(window.StaxJs);
      return;
    }
    const existing = document.querySelector(`script[src="${STAX_JS_SRC}"]`);
    if (existing) {
      existing.addEventListener('load', () => {
        if (window.StaxJs) resolve(window.StaxJs);
        else reject(new Error('Stax.js loaded but StaxJs is unavailable.'));
      });
      existing.addEventListener('error', () => reject(new Error('Could not load Stax.js.')));
      // If it already finished loading before we attached listeners.
      if (window.StaxJs) resolve(window.StaxJs);
      return;
    }
    const s = document.createElement('script');
    s.src = STAX_JS_SRC;
    s.async = true;
    s.onload = () => {
      if (window.StaxJs) resolve(window.StaxJs);
      else reject(new Error('Stax.js loaded but StaxJs is unavailable.'));
    };
    s.onerror = () => reject(new Error('Could not load Stax.js.'));
    document.body.appendChild(s);
  });
}

export default function CardSetup() {
  // ---- Token (Phase 3.x): the page is gated by a valid card-setup link ----
  const [token] = useState(() => {
    try {
      return new URLSearchParams(window.location.search).get('token') || '';
    } catch {
      return '';
    }
  });

  // linkState: missing | loading | valid | invalid
  const [linkState, setLinkState] = useState(token ? 'loading' : 'missing');
  const [fitterName, setFitterName] = useState('');
  const [fitterEmail, setFitterEmail] = useState('');

  // A TEST record simulates this step. The page still renders the real card UI so the
  // walkthrough shows what a fitter sees, but Stax.js is NEVER loaded and no card number is
  // ever collected — see the guard in the init effect below.
  const [isTest, setIsTest] = useState(false);
  // A FACILITY PARTNER uses this same page straight after signing its agreement. The backend
  // says so (kind) and sends what the card will be charged for; a fitter's page is unchanged.
  const [recordKind, setRecordKind] = useState('');
  const [lineItems, setLineItems] = useState([]);
  const isFacility = recordKind === 'facility';
  const who = isFacility ? 'facility partner' : 'fitter';
  // Which path a TEST record is taking. null until chosen, so a test walkthrough always
  // stops and asks rather than defaulting into a live merchant account.
  const [testPath, setTestPath] = useState(null);   // null | 'simulate' | 'real'
  // The references Stax returns, shown after a REAL card vaults so Randall can find it.
  const [vaulted, setVaulted] = useState(null);

  // ---- the fields Stax needs that are NOT its hosted iframes ----------------------------
  // Only the card number and CVV live inside Stax's secure iframes. Everything tokenize()
  // requires besides those is an ordinary input we own — which is why they were never
  // built: the two secure fields looked like the whole form, and the page shipped without
  // an expiry box at all. Stax rejected the submission with "Expiration Date Is Required"
  // for a field that did not exist on screen.
  //
  // Per Stax's tokenize contract, REQUIRED: firstname, lastname, method, month, year.
  // CONDITIONALLY required with no customer_id: address_city, address_state — plus
  // address_1 and address_zip wherever AVS is configured. We pass no customer_id, so all
  // of them are collected.
  const [cardFirst, setCardFirst] = useState('');
  const [cardLast, setCardLast] = useState('');
  const [expMonth, setExpMonth] = useState('');
  const [expYear, setExpYear] = useState('');
  const [billLine1, setBillLine1] = useState('');
  const [billCity, setBillCity] = useState('');
  const [billState, setBillState] = useState('');
  const [billZip, setBillZip] = useState('');
  const [billCountry, setBillCountry] = useState('USA');
  const [fieldErrors, setFieldErrors] = useState({});

  // Stax form state: idle | loading | ready | failed | unconfigured | simulated
  const [formState, setFormState] = useState(staxWebToken() ? 'loading' : 'unconfigured');
  const [busy, setBusy] = useState(false); // tokenizing + posting
  const [errorMsg, setErrorMsg] = useState('');
  const [done, setDone] = useState(false);

  const staxRef = useRef(null);

  // ---- 1 & 2: validate / pre-fill from the token ----
  useEffect(() => {
    let alive = true;
    if (!token) {
      setLinkState('missing');
      return undefined;
    }
    (async () => {
      try {
        const r = await axios.get(`${API}/public/agreements/card-setup/${encodeURIComponent(token)}`);
        if (!alive) return;
        if (r.data?.name) setFitterName(r.data.name);
        if (r.data?.email) setFitterEmail(r.data.email);
        setIsTest(!!r.data?.is_test);
        setRecordKind(r.data?.kind || '');
        setLineItems(Array.isArray(r.data?.line_items) ? r.data.line_items : []);
        // Prefill from the billing address given at signing — editable, because the address
        // a card bills to is the cardholder's business and need not be the one on file.
        const b = r.data?.billing || {};
        if (b.line1) setBillLine1(b.line1);
        if (b.city) setBillCity(b.city);
        if (b.state) setBillState(String(b.state).slice(0, 2).toUpperCase());
        if (b.zip) setBillZip(b.zip);
        // Split the name on file as a starting point for the cardholder fields.
        const parts = String(r.data?.name || '').trim().split(/\s+/).filter(Boolean);
        if (parts.length) {
          setCardFirst(parts[0]);
          if (parts.length > 1) setCardLast(parts.slice(1).join(' '));
        }
        setLinkState('valid');
      } catch {
        if (alive) setLinkState('invalid');
      }
    })();
    return () => {
      alive = false;
    };
  }, [token]);

  // ONE CONDITION decides whether the secure card field is live, and everything that
  // depends on that reads THIS — the field's own existence, the Stax.js load, and which
  // submit button appears.
  //
  // It exists because the alternative failed in the worst available way. Four render
  // conditions each tested `isTest` alone while the Stax.js load tested `testPath`, so a
  // test record that chose "Enter a real card" got a REAL, LIVE Stax iframe mounted
  // underneath the words "Secure field — not loaded for a test record", and the only
  // button on the page was "Simulate". Randall could have typed his own card into a live
  // field the page told him was inert, then pressed a button that discarded it. Two
  // sources of truth about whether a field is live is not a style question.
  //
  // The container div is now rendered by this value too, so when it says "not loaded"
  // there is no element for Stax to mount into. The label and the thing it describes
  // cannot disagree, because one does not exist without the other.
  const cardFieldLive = !isTest || testPath === 'real';

  // ---- 3: load + init Stax.js once the link is valid and configured ----
  useEffect(() => {
    if (linkState !== 'valid') return undefined;
    // THE BYPASS, and the whole of it. A test record returns BEFORE loadStaxJs() is called, so
    // the script tag is never injected, no secure iframe is mounted, tokenize() is unreachable,
    // and there is nowhere on the page a card number could be typed. Simulating by feeding fake
    // digits to the real form would still be a live call to Stax's vault.
    // A test record stops here UNTIL it has chosen. 'simulate' keeps the full bypass: the
    // script tag is never injected, no iframe mounts, tokenize() is unreachable, and there is
    // nowhere a card number could be typed. 'real' deliberately falls through to the same
    // code path a live fitter takes, because the only honest way to test a live vault is to
    // use it.
    if (!cardFieldLive) {
      setFormState(testPath === 'simulate' ? 'simulated' : 'choose');
      return undefined;
    }
    if (!staxWebToken()) {
      setFormState('unconfigured');
      return undefined;
    }
    let alive = true;
    setFormState('loading');
    (async () => {
      try {
        const StaxJs = await loadStaxJs();
        if (!alive) return;
        const stax = new StaxJs(staxWebToken(), STAX_FIELDS);
        staxRef.current = stax;
        await stax.showCardForm();
        if (!alive) return;
        setFormState('ready');
      } catch (err) {
        if (alive) {
          setFormState('failed');
          setErrorMsg(
            (err && err.message) ||
              'We could not load the secure card form. Please refresh, or contact Henry-Griffitts.'
          );
        }
      }
    })();
    return () => {
      alive = false;
    };
  }, [linkState, isTest, testPath, cardFieldLive]);

  // The simulated confirm. It posts NO card references at all — the backend fills in
  // TEST-SIMULATED-PM / TEST-SIMULATED-CUSTOMER itself, and only for a record already marked
  // is_test. So this button cannot fabricate a payment method on a real fitter even if it were
  // called with a real token: the authority is the record, not the request.
  const onSimulate = useCallback(async () => {
    if (busy) return;
    setBusy(true); setErrorMsg('');
    try {
      await axios.post(`${API}/public/agreements/card-confirm`, { token });
      setDone(true);
    } catch (err) {
      setErrorMsg(err?.response?.data?.detail || 'Could not complete the simulated card step.');
    } finally {
      setBusy(false);
    }
  }, [busy, token]);

  // Validation for the fields WE own. Stax validates the number and CVV inside its own
  // iframes; everything else is ours to check before we ever call tokenize().
  const validateCardFields = useCallback(() => {
    const e = {};
    if (!cardFirst.trim()) e.cardFirst = 'Required.';
    if (!cardLast.trim()) e.cardLast = 'Required — Stax needs a surname.';
    if (!/^(0[1-9]|1[0-2])$/.test(expMonth)) e.expMonth = 'Pick the month.';
    if (!/^\d{4}$/.test(expYear)) {
      e.expYear = 'Pick the year.';
    } else {
      // An expired card is rejected by the processor; say so here instead.
      const now = new Date();
      const endOfMonth = new Date(Number(expYear), Number(expMonth || '12'), 0, 23, 59, 59);
      if (expMonth && endOfMonth < now) e.expYear = 'That date is in the past.';
    }
    if (!billLine1.trim()) e.billLine1 = 'Required for address verification.';
    if (!billCity.trim()) e.billCity = 'Required.';
    if (!/^[A-Za-z]{2}$/.test(billState.trim())) e.billState = 'Two letters, e.g. ID.';
    if (!billZip.trim()) e.billZip = 'Required for address verification.';
    return e;
  }, [cardFirst, cardLast, expMonth, expYear, billLine1, billCity, billState, billZip]);

  // ---- 4 & 5: tokenize in the iframe, then POST only Stax's references ----
  const onSave = useCallback(async () => {
    if (busy) return;
    const stax = staxRef.current;
    if (!stax || formState !== 'ready') return;
    setBusy(true);
    setErrorMsg('');

    // VALIDATE HERE, not at Stax. A missing field must be caught beside the field, with a
    // message naming it — Stax's rejection said "Expiration Date Is Required" about a box
    // that was not on the page, which is unactionable.
    const errs = validateCardFields();
    setFieldErrors(errs);
    if (Object.keys(errs).length) {
      setErrorMsg('Please complete the highlighted fields.');
      setBusy(false);
      return;
    }

    const extraDetails = {
      // Stax's required set. `method` and the expiry were missing entirely, which is why
      // tokenize() was rejected. NONE of this is card data — the number and CVV never
      // leave Stax's iframes and this code cannot read them.
      method: 'card',
      firstname: cardFirst.trim().slice(0, 50),
      lastname: cardLast.trim().slice(0, 50),
      month: expMonth,                      // 2-digit, e.g. "05"
      year: expYear,                        // 4-digit, e.g. "2029"
      // Conditionally required because we pass no customer_id, and for AVS.
      address_1: billLine1.trim(),
      address_city: billCity.trim(),
      address_state: billState.trim().toUpperCase().slice(0, 2),
      address_zip: billZip.trim(),
      address_country: (billCountry || 'USA').trim(),
      email: fitterEmail || '',
      // Stax does its own validation server-side; ours runs first so the message lands
      // next to the field rather than arriving as a rejection.
      validate: false,
    };

    let resp;
    try {
      resp = await stax.tokenize(extraDetails);
    } catch (err) {
      // Never display or log the card number — only a generic message.
      setErrorMsg(
        (err && (err.message || err.error)) ||
          'We could not save your card. Please check the number and try again.'
      );
      setBusy(false);
      return;
    }

    // Pull ONLY Stax's returned references — no raw card data exists here.
    const paymentMethodId = resp?.id || '';
    const customerId = resp?.customer?.id || resp?.customer_id || '';
    const cardLastFour = resp?.card_last_four || '';
    const cardType = resp?.card_type || '';

    if (!paymentMethodId || !customerId) {
      setErrorMsg('We could not confirm your card with our payment provider. Please try again or contact Henry-Griffitts.');
      setBusy(false);
      return;
    }

    try {
      const r = await axios.post(`${API}/public/agreements/card-confirm`, {
        token,
        payment_method_id: paymentMethodId,
        customer_id: customerId,
        card_last_four: cardLastFour,
        card_type: cardType,
      });
      // Keep the references so the done screen can show them. The Hub never removes a card
      // from Stax, so whoever stored it needs these to find it in the dashboard themselves.
      if (!r.data?.simulated) {
        setVaulted({
          paymentMethodId: r.data?.payment_method_id || paymentMethodId,
          customerId: r.data?.customer_id || customerId,
          lastFour: r.data?.card_last_four || cardLastFour,
          brand: r.data?.card_type || cardType,
        });
      }
      setDone(true);
    } catch (err) {
      setErrorMsg(
        err?.response?.data?.detail ||
          'Your card was saved with our payment provider, but we could not finish on our end. Please contact Henry-Griffitts.'
      );
    } finally {
      setBusy(false);
    }
    // Every field this reads must be in the dependency list, or the callback closes over
    // the values from first render — which for a form means submitting the empty strings
    // the page started with while the user watches their own typing on screen.
  }, [busy, formState, fitterEmail, token, validateCardFields,
      cardFirst, cardLast, expMonth, expYear,
      billLine1, billCity, billState, billZip, billCountry]);

  // ---- Render ----
  const showForm = linkState === 'valid' && !done;

  return (
    <div data-testid="card-setup-page">
      <style>{`
        .cs-root{
          --green:#48B64C; --green-d:#3A9B3E; --green-deep:#48B64C;
          /* WHICH TOKEN GOES ON WHICH GROUND — the pair below is not interchangeable.
             --ink / --slate / --grey are LIGHT text, for the DARK surfaces (--paper,
             --wash). --field is the one LIGHT surface on this page, because Stax's secure
             iframes need a light background, and text on it must be DARK (#1A1A1A).
             Putting --ink on --field gives #E8E8E8 on #FDFDFB: 1.05:1, invisible, and
             exactly how Randall came to type his card details into fields he could not
             read. The token is named for its ink, not its ground, so the mistake looks
             right until it is measured. Measure it. */
          --ink:#E8E8E8; --slate:#C2C2C2; --grey:#8C8C8C;
          --paper:#2A2A2A; --wash:#1E1E1E; --field:#FDFDFB; --line:#383838;
          --amber:#E0B45C; --amber-bg:rgba(224,180,92,.12);
          --shadow:0 1px 2px rgba(30,50,20,.04),0 8px 30px rgba(30,50,20,.07);
          font-family:-apple-system,BlinkMacSystemFont,"Segoe UI",Roboto,Helvetica,Arial,sans-serif;
          background:
            radial-gradient(1200px 600px at 50% -200px,rgba(72,182,76,.06),transparent),
            #1E1E1E;
          color:var(--slate);line-height:1.55;-webkit-font-smoothing:antialiased;
          min-height:100vh;padding:0 16px 80px;
        }
        .cs-root *{box-sizing:border-box;margin:0;padding:0}
        @media (prefers-reduced-motion:reduce){.cs-root *{animation:none!important;transition:none!important}}
        .cs-root .topbar{max-width:620px;margin:0 auto;padding:22px 4px 18px;display:flex;align-items:center;justify-content:space-between;gap:16px}
        .cs-root .brand{display:flex;align-items:center;gap:11px}
        .cs-root .brand svg{display:block}
        /* Same mark, same rule as the signing flow — see FitterSigning.js. */
        .cs-root .treemark{display:block;height:30px;width:auto}
        @media(max-width:480px){.cs-root .treemark{height:26px}}
        .cs-root .brand .wm{font-weight:700;letter-spacing:.12em;font-size:13px;color:var(--ink);text-transform:uppercase}
        .cs-root .securetag{font-size:12px;color:var(--green-deep);background:rgba(72,182,76,.12);border:1px solid rgba(72,182,76,.25);padding:5px 11px;border-radius:100px;display:flex;align-items:center;gap:6px;font-weight:600;white-space:nowrap}
        .cs-root .securetag svg{display:block}
        .cs-root .sheet{max-width:620px;margin:0 auto;background:var(--paper);border:1px solid var(--line);border-radius:18px;box-shadow:var(--shadow);overflow:hidden}
        .cs-root .sheet-head{padding:30px 38px 24px;border-bottom:1px solid var(--line);background:var(--paper);position:relative}
        .cs-root .sheet-head::before{content:"";position:absolute;left:0;top:0;bottom:0;width:5px;background:linear-gradient(180deg,var(--green),var(--green-deep))}
        .cs-root .eyebrow{font-size:12px;font-weight:700;letter-spacing:.14em;text-transform:uppercase;color:var(--green);margin-bottom:8px}
        .cs-root .sheet-head h1{font-size:25px;color:var(--ink);font-weight:750;letter-spacing:-.01em;line-height:1.2}
        .cs-root .sheet-head p{margin-top:8px;color:var(--slate);font-size:14.5px;max-width:54ch}
        .cs-root .sheet-body{padding:30px 38px 34px}
        @media(max-width:560px){.cs-root .sheet-head,.cs-root .sheet-body{padding-left:22px;padding-right:22px}}
        .cs-root .grouptitle{font-size:13px;font-weight:700;letter-spacing:.04em;text-transform:uppercase;color:var(--green-deep);margin:0 0 12px;padding-bottom:7px;border-bottom:1px solid var(--line)}
        .cs-root .f{display:flex;flex-direction:column;gap:5px;margin-bottom:15px}
        .cs-root label{font-size:12.5px;font-weight:600;color:var(--ink)}
        /* Stax mounts its secure iframe inside these containers. Style the box; the iframe lives within. */
        .cs-root .stax-field{font:inherit;background:var(--field);border:1px solid #D8D8D0;border-radius:9px;padding:0 12px;height:44px;display:flex;align-items:center;transition:border-color .2s,box-shadow .2s}
        .cs-root .stax-field iframe{border:0;width:100%;height:42px;display:block}
        .cs-root .stax-field.focused{border-color:var(--green);box-shadow:0 0 0 3px rgba(72,182,76,.15)}
        .cs-root .row{display:grid;grid-template-columns:1fr 1fr;gap:14px}
        @media(max-width:440px){.cs-root .row{grid-template-columns:1fr}}
        /* The fields WE own — expiry, cardholder name, billing address. Styled to match the
           Stax iframe containers above so the form reads as one thing, not two. */
        /* #1A1A1A, the SAME literal STAX_FIELDS passes to Stax for the card number and CVV,
           so all five fields a person types into look identical.

           NOT var(--ink). That is #E8E8E8 — a near-white intended for text on the dark
           --paper surface — and these inputs sit on --field (#FDFDFB, near-white). It was
           inherited from the '.cs-root label' rule when these styles were written, giving
           1.05:1 contrast: Randall typed his card details into fields whose text he could
           barely see, and reported them as "grey instead of black". A theme variable is
           only correct against the surface it was chosen for. */
        .cs-root input[type=text],.cs-root select{
          font:inherit;font-size:15px;color:#1A1A1A;background:var(--field);
          border:1px solid #D8D8D0;border-radius:9px;padding:0 12px;height:44px;
          width:100%;outline:none;transition:border-color .2s,box-shadow .2s}
        .cs-root input[type=text]::placeholder{color:#8A8F88}
        .cs-root select option{color:#1A1A1A;background:#FDFDFB}
        .cs-root input[type=text]:focus,.cs-root select:focus{
          border-color:var(--green);box-shadow:0 0 0 3px rgba(72,182,76,.15)}
        /* iOS zooms a focused input under 16px, which leaves the page scrolled sideways. */
        @media(max-width:560px){.cs-root input[type=text],.cs-root select{font-size:16px}}
        .cs-root .fielderr{font-size:12px;color:#B23B3B;margin-top:5px;font-weight:600}
        .cs-root .f:has(.fielderr) input[type=text],
        .cs-root .f:has(.fielderr) select{border-color:#C05A5A}
        .cs-root .pci{display:flex;align-items:center;gap:8px;font-size:12px;color:var(--grey);margin-top:6px}
        .cs-root .pci svg{flex:none}
        .cs-root .save{margin-top:22px;width:100%;font:inherit;font-size:15px;font-weight:650;cursor:pointer;border-radius:11px;padding:14px 24px;transition:all .2s;border:1px solid transparent;background:linear-gradient(180deg,var(--green),var(--green-d));color:#fff;box-shadow:0 4px 14px rgba(74,140,40,.3);display:flex;align-items:center;justify-content:center;gap:10px}
        .cs-root .save:hover:not(:disabled){transform:translateY(-1px);box-shadow:0 6px 18px rgba(74,140,40,.36)}
        .cs-root .save:disabled{background:#3A3A3A;box-shadow:none;cursor:not-allowed;transform:none;color:#fff}
        .cs-root .spinner{width:16px;height:16px;border-radius:50%;border:2.4px solid rgba(255,255,255,.45);border-top-color:#fff;animation:cs-spin .7s linear infinite;flex:none}
        @keyframes cs-spin{to{transform:rotate(360deg)}}
        .cs-root .formspin{display:flex;align-items:center;gap:11px;color:var(--grey);font-size:13.5px;padding:18px 0}
        .cs-root .formspin .spinner{border-color:rgba(72,182,76,.25);border-top-color:var(--green)}
        .cs-root .errnote{margin-top:16px;font-size:13.5px;color:#F08A80;background:rgba(224,82,82,.12);border:1px solid rgba(224,82,82,.4);border-radius:10px;padding:11px 14px}
        .cs-root .warnnote{margin-top:4px;font-size:13.5px;color:var(--amber);background:var(--amber-bg);border:1px solid #EAD9A8;border-radius:10px;padding:13px 16px}
        /* ---- the two-path chooser for a test record --------------------------------
           Deliberately two large blocks rather than a radio pair: the choice is between
           "touches nothing" and "puts a real card in the live vault", and that is not a
           setting, it is a decision. Both are 44px+ and readable on a phone. */
        .cs-root .pathcard{display:block;width:100%;text-align:left;font:inherit;cursor:pointer;
          border:1.5px solid var(--line);border-radius:13px;background:var(--card,#242424);
          padding:16px 18px;margin-top:12px;transition:border-color .2s,background .2s}
        .cs-root .pathcard:hover{border-color:var(--green)}
        .cs-root .pathtitle{display:block;font-size:15px;font-weight:700;color:var(--ink);margin-bottom:7px}
        .cs-root .pathbody{display:block;font-size:13px;color:var(--slate);line-height:1.55}
        .cs-root .pathcard-live{border-color:#C05A5A;background:rgba(192,90,90,.10)}
        .cs-root .pathcard-live:hover{border-color:#E07A7A}
        .cs-root .pathcard-live .pathtitle{color:#F0A0A0}
        /* ---- the vault references on the done screen ------------------------------- */
        .cs-root .vaultnote{margin-top:22px;text-align:left;border:1.5px solid #C05A5A;
          border-radius:13px;background:rgba(192,90,90,.10);padding:16px 18px}
        .cs-root .vaulthead{font-size:14px;font-weight:750;color:#F0A0A0;margin-bottom:8px}
        .cs-root .vaultnote p{font-size:13px;color:var(--slate);margin:0 0 12px;max-width:none}
        .cs-root .vaultrefs{margin:0 0 12px;font-size:13px}
        .cs-root .vaultrefs div{display:flex;flex-wrap:wrap;gap:4px 10px;padding:6px 0;
          border-top:1px solid rgba(192,90,90,.28)}
        .cs-root .vaultrefs dt{color:var(--grey);min-width:140px}
        .cs-root .vaultrefs dd{margin:0;color:var(--ink);font-weight:600;word-break:break-all}
        .cs-root .vaultrefs code{font-family:ui-monospace,SFMono-Regular,Menlo,monospace;font-size:12.5px}
        .cs-root .vaulthow{font-size:13px;color:var(--slate);margin:0}
        @media(max-width:480px){.cs-root .vaultrefs dt{min-width:0;width:100%}}
        .cs-root .done{text-align:center;padding:18px 0 8px}
        .cs-root .done .seal{width:88px;height:88px;margin:0 auto 22px;border-radius:50%;background:linear-gradient(135deg,var(--green),var(--green-deep));display:flex;align-items:center;justify-content:center;box-shadow:0 10px 30px rgba(74,140,40,.35);animation:cs-pop .5s cubic-bezier(.2,1.3,.4,1)}
        @keyframes cs-pop{from{transform:scale(.5);opacity:0}to{transform:scale(1);opacity:1}}
        .cs-root .done h2{font-size:23px;color:var(--ink);font-weight:750;margin-bottom:8px}
        .cs-root .done p{color:var(--slate);font-size:14.5px;max-width:42ch;margin:0 auto 6px}
        .cs-root .hint{font-size:12px;color:var(--grey);text-align:center;max-width:620px;margin:18px auto 0;display:flex;align-items:center;justify-content:center;gap:7px}
      `}</style>

      <div className="cs-root">
        <div className="topbar">
          <div className="brand">
            {/* The real Henry-Griffitts tree mark, colour-isolated from the wordmark. The
                shape it replaces was a hand-drawn stand-in: not our tree, and drawn for a
                light page that no longer exists. */}
            <img src={hgTree} alt="" aria-hidden="true" className="treemark" />
            <span className="wm hg-wordmark">Henry&#8209;Griffitts</span>
          </div>
          <div className="securetag">
            <svg width="12" height="13" viewBox="0 0 12 13" fill="none">
              <path d="M6 1l4 1.6v3.2C10 9 8.2 11 6 12 3.8 11 2 9 2 5.8V2.6L6 1z" stroke="#2F5A1B" strokeWidth="1.3" strokeLinejoin="round" />
            </svg>
            Secure card setup
          </div>
        </div>

        <main className="sheet">
          {done ? (
            /* ---- Success ---- */
            <div className="sheet-body">
              <div className="done" data-testid="cs-success">
                <div className="seal">
                  <svg width="40" height="40" viewBox="0 0 40 40"><path d="M11 21l6 6 12-14" stroke="#fff" strokeWidth="3.4" fill="none" strokeLinecap="round" strokeLinejoin="round" /></svg>
                </div>
                <h2>Your card is on file — you&rsquo;re all set.</h2>
                <p>{recordKind === 'facility'
                  ? 'Thank you. Henry-Griffitts will charge the start-up fee to this card and countersign your Facility Partner Agreement.'
                  : 'Your Henry-Griffitts account is now active. Thank you.'}</p>

                {/* A REAL card just went into the LIVE vault, and the Hub will never take it
                    out. Whoever stored it needs the references to find it, so they are shown
                    here rather than left to be dug out of a database. These identify the one
                    payment method just created — not card data, not anyone else's record. */}
                {vaulted && (
                  <div className="vaultnote" data-testid="cs-vaulted-refs">
                    <div className="vaulthead">A real card was stored — you remove it yourself</div>
                    <p>
                      This went into the <b>live Henry-Griffitts merchant account</b>. The Hub is
                      add-only against our payment provider and will never delete it, so it stays
                      until you remove it.
                    </p>
                    <dl className="vaultrefs">
                      <div><dt>Card</dt><dd>{vaulted.brand || 'card'} ending {vaulted.lastFour || '????'}</dd></div>
                      <div><dt>Payment method id</dt><dd><code>{vaulted.paymentMethodId}</code></dd></div>
                      <div><dt>Customer id</dt><dd><code>{vaulted.customerId}</code></dd></div>
                    </dl>
                    <p className="vaulthow">
                      <b>To remove it:</b> sign in to the Stax dashboard, then
                      {' '}<b>Payments &rarr; Customers</b> &rarr; find the customer by the id above
                      {' '}&rarr; open <b>Payment Methods</b> &rarr; delete the one ending
                      {' '}<b>{vaulted.lastFour || '????'}</b>. Take a screenshot of this before
                      you close the page.
                    </p>
                  </div>
                )}
              </div>
            </div>
          ) : linkState === 'missing' || linkState === 'invalid' ? (
            /* ---- Invalid / missing link ---- */
            <>
              <div className="sheet-head">
                <div className="eyebrow">Secure card setup</div>
                <h1>{linkState === 'missing' ? 'No card-setup link' : 'Link not valid'}</h1>
                <p>
                  {linkState === 'missing'
                    ? 'This page needs a secure link from Henry-Griffitts. Please open the link from your email, or contact Henry-Griffitts (hgsupport@henry-griffitts.com).'
                    : 'This card-setup link is invalid or has already been used. Please contact Henry-Griffitts (hgsupport@henry-griffitts.com) and we’ll send you a fresh link.'}
                </p>
              </div>
            </>
          ) : linkState === 'loading' ? (
            /* ---- Validating the token ---- */
            <>
              <div className="sheet-head">
                <div className="eyebrow">Secure card setup</div>
                <h1>Opening your secure link…</h1>
                <p>One moment while we verify your card-setup link.</p>
              </div>
            </>
          ) : (
            /* ---- Valid: the Stax card form ---- */
            <>
              <div className="sheet-head">
                <div className="eyebrow">Secure card setup</div>
                <h1>{fitterName ? `Adding a card for ${fitterName}` : 'Add your card on file'}</h1>
                <p>Enter your card details below. Your card is stored securely with our payment provider — Henry-Griffitts never sees or stores your card number.</p>
                {recordKind === 'facility' && lineItems.length > 0 && (
                  <div data-testid="cs-line-items" style={{ marginTop: 14, border: '1px solid var(--line)', borderRadius: 12, padding: '12px 14px' }}>
                    <div style={{ fontSize: 12, fontWeight: 700, letterSpacing: '.06em', textTransform: 'uppercase', color: 'var(--green-deep)', marginBottom: 8 }}>What this card will be charged for</div>
                    {lineItems.map((i) => (
                      <div key={i.label} style={{ display: 'flex', justifyContent: 'space-between', gap: 12, fontSize: 14, padding: '3px 0' }}>
                        <span>{i.label} <span style={{ color: 'var(--grey)' }}>· {i.due}</span></span>
                        <b>{i.amount}</b>
                      </div>
                    ))}
                    <div style={{ fontSize: 12, color: 'var(--grey)', marginTop: 6 }}>Nothing is charged on this page. Henry-Griffitts charges these to the card on file, as your agreement sets out.</div>
                  </div>
                )}
              </div>

              <div className="sheet-body">
                {/* BEFORE A PATH IS CHOSEN the page cannot claim what will happen — one of the
                    two options below stores a real card. This notice states only what is true
                    at this moment: it is a test record and the choice has not been made. */}
                {isTest && formState === 'choose' && (
                  <div
                    data-testid="cs-test-notice"
                    style={{ background: '#FDF4E3', border: '2px solid #D9A441', borderRadius: 3,
                             color: '#5C4108', padding: '14px 16px', marginBottom: 18,
                             fontSize: 13, lineHeight: 1.5 }}
                  >
                    <div style={{ fontWeight: 700, letterSpacing: '.04em', marginBottom: 4 }}>
                      TEST RECORD — CHOOSE HOW TO RUN THIS STEP
                    </div>
                    This is a walkthrough of a real {who}&rsquo;s card step. What happens next
                    depends on which option you pick below: one sends nothing to our payment
                    provider, the other stores a real card in the live vault. Read both.
                  </div>
                )}

                {/* THE BANNER. Unmissable and above everything: the one page in the flow where a
                    step is bypassed, so it must be impossible to look at this screen and believe
                    a card was taken. #6B2020 on #FDECEC is 8.02:1; the border is 3.3:1.

                    Gated on the SIMULATE path specifically, not on `testPath !== 'real'` — that
                    also matched the not-yet-chosen state, so "NO CARD IS BEING COLLECTED" was
                    rendering directly above the button offering to collect a real one. */}
                {isTest && testPath === 'simulate' && (
                  <div
                    data-testid="cs-test-banner"
                    style={{ background: '#FDECEC', border: '2px solid #C05A5A', borderRadius: 3,
                             color: '#6B2020', padding: '14px 16px', marginBottom: 18,
                             fontSize: 13, lineHeight: 1.5 }}
                  >
                    <div style={{ fontWeight: 700, letterSpacing: '.04em', marginBottom: 4 }}>
                      TEST RECORD — NO CARD IS BEING COLLECTED
                    </div>
                    This is a walkthrough of a real {who}&rsquo;s card step. Our payment
                    provider&rsquo;s secure form is <b>not loaded on this page</b>, there is
                    nowhere to type a card number, and nothing will be sent to them or charged.
                    The card fields below are shown greyed out so you can see what a {who} sees.
                  </div>
                )}

                {/* THE CHOICE. A test record stops here rather than defaulting either way. The
                    two paths differ in a way that cannot be undone by us: one touches nothing,
                    the other puts a real card into Henry-Griffitts' live merchant vault, and
                    the Hub is add-only against Stax so it can never take it back out. */}
                {isTest && formState === 'choose' && (
                  <div data-testid="cs-path-choice">
                    <div className="grouptitle">How do you want to test this step?</div>

                    <button
                      type="button"
                      data-testid="cs-choose-simulate"
                      className="pathcard"
                      onClick={() => setTestPath('simulate')}
                    >
                      <span className="pathtitle">Simulate it — nothing is sent</span>
                      <span className="pathbody">
                        The secure card form is never loaded and there is nowhere to type a card
                        number. {isFacility
                          ? <>The facility record stores <b>TEST-SIMULATED-PM</b> as its card on file and stays Signed until the start-up fee is marked charged.</>
                          : <>Your account activates with <b>TEST-SIMULATED-PM</b> recorded
                        instead of a card.</>} Nothing reaches our payment provider. This is the
                        safe choice and it exercises the whole flow.
                      </span>
                    </button>

                    <button
                      type="button"
                      data-testid="cs-choose-real"
                      className="pathcard pathcard-live"
                      onClick={() => setTestPath('real')}
                    >
                      <span className="pathtitle">Enter a real card — this is the live account</span>
                      <span className="pathbody">
                        <b>Read this before choosing.</b> This loads our payment provider&rsquo;s
                        real form and stores a real card in the <b>live Henry-Griffitts merchant
                        vault</b> — the same one holding every existing fitter&rsquo;s card.
                        There is no sandbox and test card numbers will not work; any number you
                        type is treated as a real card.
                        <br /><br />
                        <b>You remove it yourself afterwards.</b> The Hub never deletes anything
                        from the vault, by design. We&rsquo;ll show you the reference ids you need
                        to find and delete it in the Stax dashboard.
                        <br /><br />
                        Adding a card may place a <b>small temporary authorisation hold</b> — a
                        pending line on your statement that disappears on its own. That is
                        expected, not a charge.
                      </span>
                    </button>
                  </div>
                )}

                {formState === 'choose' ? null : formState === 'unconfigured' ? (
                  <div className="warnnote">
                    Card setup isn&rsquo;t configured yet — please contact Henry-Griffitts (hgsupport@henry-griffitts.com) and we&rsquo;ll get you set up.
                  </div>
                ) : (
                  <>
                    <div className="grouptitle">Card details</div>

                    {/* THE MOUNT POINT EXISTS ONLY WHEN THE FIELD IS LIVE.
                        `id="card-number"` is the element Stax.js mounts its secure iframe
                        into (STAX_FIELDS.number.id). When cardFieldLive is false that id is
                        not in the document at all, so there is nothing for Stax to mount
                        into even if the script were loaded — the inert label cannot be
                        wrong about the field, because the field does not exist beside it.
                        Previously both rendered together and a live iframe sat under text
                        reading "not loaded for a test record". */}
                    <div className="f">
                      <label htmlFor="card-number">Card number</label>
                      {cardFieldLive
                        ? <div className="stax-field" id="card-number" />
                        : <div className="stax-field" data-testid="cs-inert-number"
                               style={{ background: '#F0F0ED', color: '#5F6360',
                                        display: 'flex', alignItems: 'center',
                                        padding: '0 14px', fontSize: 13 }}>
                            Secure field — not loaded for a test record
                          </div>}
                    </div>

                    <div className="row">
                      <div className="f">
                        <label htmlFor="card-cvv">Security code (CVV)</label>
                        {cardFieldLive
                          ? <div className="stax-field" id="card-cvv" />
                          : <div className="stax-field" data-testid="cs-inert-cvv"
                                 style={{ background: '#F0F0ED', color: '#5F6360',
                                          display: 'flex', alignItems: 'center',
                                          padding: '0 14px', fontSize: 13 }}>
                              Not loaded
                            </div>}
                      </div>
                    </div>

                    {/* THE FIELDS STAX NEEDS THAT ARE NOT ITS IFRAMES.
                        Only the number and CVV are Stax-hosted. Expiry, cardholder name and
                        the billing address are ordinary inputs we own, and the page shipped
                        without any of them — so tokenize() was rejected for an expiry box
                        that did not exist. Rendered only on the live path: on the simulate
                        path there is nothing to tokenize and asking for a real billing
                        address would be theatre. */}
                    {cardFieldLive && (
                      <div data-testid="cs-owned-fields">
                        <div className="row">
                          <Fld label="Expiry month" err={fieldErrors.expMonth}>
                            <select data-testid="cs-exp-month" value={expMonth}
                                    onChange={(e) => setExpMonth(e.target.value)}>
                              <option value="">Month</option>
                              {MONTHS.map((m) => <option key={m} value={m}>{m}</option>)}
                            </select>
                          </Fld>
                          <Fld label="Expiry year" err={fieldErrors.expYear}>
                            <select data-testid="cs-exp-year" value={expYear}
                                    onChange={(e) => setExpYear(e.target.value)}>
                              <option value="">Year</option>
                              {YEARS.map((y) => <option key={y} value={y}>{y}</option>)}
                            </select>
                          </Fld>
                        </div>

                        <div className="grouptitle" style={{ marginTop: 18 }}>Name on the card</div>
                        <div className="row">
                          <Fld label="First name" err={fieldErrors.cardFirst}>
                            <input type="text" data-testid="cs-card-first" value={cardFirst}
                                   autoComplete="cc-given-name"
                                   onChange={(e) => setCardFirst(e.target.value)} />
                          </Fld>
                          <Fld label="Last name" err={fieldErrors.cardLast}>
                            <input type="text" data-testid="cs-card-last" value={cardLast}
                                   autoComplete="cc-family-name"
                                   onChange={(e) => setCardLast(e.target.value)} />
                          </Fld>
                        </div>

                        <div className="grouptitle" style={{ marginTop: 18 }}>Billing address</div>
                        <p style={{ fontSize: 12.5, color: 'var(--grey)', margin: '-6px 0 12px' }}>
                          The address your card bills to. Prefilled from your agreement — change
                          it if the card bills somewhere else.
                        </p>
                        <Fld label="Street address" err={fieldErrors.billLine1}>
                          <input type="text" data-testid="cs-bill-line1" value={billLine1}
                                 autoComplete="billing address-line1"
                                 onChange={(e) => setBillLine1(e.target.value)} />
                        </Fld>
                        <div className="row">
                          <Fld label="City" err={fieldErrors.billCity}>
                            <input type="text" data-testid="cs-bill-city" value={billCity}
                                   autoComplete="billing address-level2"
                                   onChange={(e) => setBillCity(e.target.value)} />
                          </Fld>
                          <Fld label="State" err={fieldErrors.billState}>
                            <input type="text" data-testid="cs-bill-state" value={billState}
                                   maxLength={2} placeholder="ID"
                                   autoComplete="billing address-level1"
                                   onChange={(e) => setBillState(e.target.value.toUpperCase())} />
                          </Fld>
                        </div>
                        <div className="row">
                          <Fld label="ZIP / postal code" err={fieldErrors.billZip}>
                            <input type="text" data-testid="cs-bill-zip" value={billZip}
                                   autoComplete="billing postal-code"
                                   onChange={(e) => setBillZip(e.target.value)} />
                          </Fld>
                          <Fld label="Country">
                            <input type="text" data-testid="cs-bill-country" value={billCountry}
                                   onChange={(e) => setBillCountry(e.target.value)} />
                          </Fld>
                        </div>
                      </div>
                    )}

                    {/* ONE button, and it says exactly what it does. Gated on the SIMULATE
                        path explicitly — `isTest` alone also matched a test record that had
                        chosen "Enter a real card", which is how the only button on that
                        screen came to be "Simulate". */}
                    {isTest && testPath === 'simulate' && showForm && (
                      <>
                        <button
                          type="button"
                          className="save"
                          data-testid="cs-simulate"
                          onClick={onSimulate}
                          disabled={busy}
                          style={{ background: '#5A3F8C', color: '#FFFFFF' }}
                        >
                          {busy ? (<><span className="spinner" />Simulating…</>)
                                : 'Simulate the card step (no card taken)'}
                        </button>
                        <div style={{ fontSize: 12, color: '#5C5C5C', marginTop: 10,
                                      lineHeight: 1.55 }} data-testid="cs-simulate-criteria">
                          <b>What should happen:</b> {isFacility ? (<>this page shows the same
                          &ldquo;you&rsquo;re all set&rdquo; confirmation a real facility partner sees, the
                          facility record stays <b>Signed</b> with <b>TEST-SIMULATED-PM</b> as its card
                          on file, and <b>nothing is emailed</b> — no activation runs for a facility.
                          Mark the start-up fee charged on the facility screen to move it to Paid.</>) : (<>this page shows the same
                          &ldquo;you&rsquo;re all set&rdquo; confirmation a real fitter sees, the
                          fitter record moves from <b>awaiting card</b> to <b>active</b>, the
                          card on file reads <b>TEST-SIMULATED-PM</b> rather than a real card,
                          and <b>Packet 1</b> arrives at randall@henry-griffitts.com — not at
                          the address on the record.</>)}
                        </div>
                      </>
                    )}

                    {formState === 'loading' && (
                      <div className="formspin">
                        <span className="spinner" />
                        Loading the secure card form…
                      </div>
                    )}

                    {formState === 'failed' && (
                      <div className="errnote" data-testid="cs-error">
                        {errorMsg || 'We could not load the secure card form. Please refresh, or contact Henry-Griffitts.'}
                      </div>
                    )}

                    {/* THE REAL PATH FAILS LOUDLY AND STOPS. It must never quietly become a
                        simulation: "he chose real, it simulated, and nothing said so" is the
                        defect this whole screen just cost two walkthroughs to find. Going
                        back to the chooser is offered as an EXPLICIT click with the
                        consequence named — a deliberate choice, not a fallback. */}
                    {isTest && testPath === 'real' && formState === 'failed' && (
                      <div data-testid="cs-real-failed" style={{
                        marginTop: 14, padding: '13px 15px', borderRadius: 3,
                        background: '#FDECEC', border: '2px solid #C05A5A',
                        color: '#6B2020', fontSize: 13, lineHeight: 1.5 }}>
                        <b>The real card form did not load, so no card can be taken on this
                        page.</b> Nothing has been sent to our payment provider and nothing has
                        been stored. This step has NOT been completed.
                        <div style={{ marginTop: 10 }}>
                          <button
                            type="button"
                            data-testid="cs-back-to-choice"
                            onClick={() => { setTestPath(null); setErrorMsg(''); }}
                            style={{ font: 'inherit', fontSize: 13, fontWeight: 650,
                                     cursor: 'pointer', borderRadius: 3, padding: '8px 14px',
                                     border: '1px solid #6B2020', background: 'transparent',
                                     color: '#6B2020' }}
                          >
                            Go back and choose again
                          </button>
                        </div>
                      </div>
                    )}

                    <div className="pci">
                      <svg width="13" height="13" viewBox="0 0 13 13" fill="none">
                        <path d="M6.5 1l4 1.6v3.4c0 3-1.8 5.2-4 6-2.2-.8-4-3-4-6V2.6L6.5 1z" stroke="#8A8F88" strokeWidth="1.2" strokeLinejoin="round" />
                      </svg>
                      Your card number is entered directly into our payment provider&rsquo;s secure fields and never touches our servers.
                    </div>

                    {/* Same condition as the field itself. `!isTest` suppressed this for a
                        test record that had chosen the real path, leaving a live Stax field
                        with no way to submit it. */}
                    {showForm && cardFieldLive && (
                      <button
                        type="button"
                        className="save"
                        data-testid="cs-save"
                        onClick={onSave}
                        disabled={busy || formState !== 'ready'}
                      >
                        {busy ? (
                          <>
                            <span className="spinner" />
                            Saving your card…
                          </>
                        ) : (
                          'Save my card'
                        )}
                      </button>
                    )}

                    {errorMsg && formState !== 'failed' && (
                      <div className="errnote" data-testid="cs-error">
                        {errorMsg}
                      </div>
                    )}
                  </>
                )}
              </div>
            </>
          )}
        </main>

        <p className="hint">
          <svg width="13" height="13" viewBox="0 0 13 13"><path d="M6.5 1l4 1.6v3.4c0 3-1.8 5.2-4 6-2.2-.8-4-3-4-6V2.6L6.5 1z" stroke="#8A8F88" strokeWidth="1.2" fill="none" strokeLinejoin="round" /></svg>
          Payments are processed securely. Henry-Griffitts · Coeur d&rsquo;Alene, Idaho
        </p>
      </div>
    </div>
  );
}
